import * as React from 'react'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material'
import IosShareIcon from '@mui/icons-material/IosShare'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import DirectionSnackbar from '../../../Components/Snackbar'
import { makeGetRequest, makePostRequest, makePatchRequest } from 'utils/api'

/**
 * LAP-614b — « Exporter vers un agenda externe ».
 *
 * Fenêtre de confirmation ouverte depuis les vues du calendrier : elle exporte
 * EXACTEMENT la période affichée (la journée, la semaine, le mois ou l'année
 * sous les yeux de l'utilisateur) vers les comptes Google / Outlook connectés.
 *
 * Avant, l'export n'était accessible qu'en créant un événement, une fois
 * l'option activée au fond des paramètres de connectivité — et il repoussait
 * tout l'historique. Ici on affiche la période, le nombre d'événements
 * concernés et le résultat compte par compte ; l'export peut aussi être activé
 * directement depuis la fenêtre (bascule par compte) sans aller dans les
 * paramètres.
 *
 * Expose handleClickOpen() via ref (même contrat que ConstraintsModal /
 * GeneratePlanningModal).
 */

// Bornes de la période affichée, à partir de la vue react-big-calendar.
// L'agenda affiche par défaut un mois glissant : on l'aligne sur le mois.
export const getViewRange = (view, date) => {
  const d = moment(date)
  switch (view) {
    case 'day':
      return { start: d.clone().startOf('day'), end: d.clone().endOf('day'), unit: 'day' }
    case 'week':
      return { start: d.clone().startOf('week'), end: d.clone().endOf('week'), unit: 'week' }
    case 'year':
      return { start: d.clone().startOf('year'), end: d.clone().endOf('year'), unit: 'year' }
    case 'agenda':
    case 'month':
    default:
      return { start: d.clone().startOf('month'), end: d.clone().endOf('month'), unit: 'month' }
  }
}

const API_DATE = 'YYYY-MM-DD'

const ExportCalendarModal = React.forwardRef((props, ref) => {
  const { view = 'month', date = new Date(), culture = 'fr' } = props
  const { t } = useTranslation('layout')
  const tr = (k, dflt, opts) =>
    t(`time.export.${k}`, { defaultValue: dflt, ...(opts || {}) })

  const [open, setOpen] = React.useState(false)
  const [loading, setLoading] = React.useState(false)
  const [exporting, setExporting] = React.useState(false)
  const [preview, setPreview] = React.useState(null)
  const [selected, setSelected] = React.useState([])
  const [result, setResult] = React.useState(null)
  const [snack, setSnack] = React.useState({ open: false, color: 'info', message: '' })

  // La période est figée à l'ouverture : si l'utilisateur navigue derrière la
  // modale, on n'exporte pas une autre période que celle annoncée.
  const [range, setRange] = React.useState(() => getViewRange(view, date))

  const browserTimezone = React.useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || ''
    } catch (e) {
      return ''
    }
  }, [])

  const showSnack = (color, message) => {
    setSnack({ open: true, color, message })
    setTimeout(() => setSnack({ open: false, color, message: '' }), 4000)
  }

  const fetchPreview = async (nextRange) => {
    setLoading(true)
    const res = await makeGetRequest(
      `/api/user/calendar-export-preview/?start_date=${nextRange.start.format(
        API_DATE,
      )}&end_date=${nextRange.end.format(API_DATE)}&view=${nextRange.unit}`,
      {},
      true,
    )
    setLoading(false)
    if (res && res.status === 200) {
      setPreview(res.data)
      // Pré-cochés : les comptes déjà autorisés à recevoir les événements Life.
      const enabled = (res.data.accounts || [])
        .filter((a) => a.sync_out_enabled)
        .map((a) => a.id)
      setSelected(enabled.length ? enabled : (res.data.accounts || []).map((a) => a.id))
    } else {
      setPreview({ accounts: [], event_count: 0 })
      showSnack('error', tr('errLoad', "Impossible de préparer l'export."))
    }
  }

  const handleClickOpen = () => {
    const nextRange = getViewRange(view, date)
    setRange(nextRange)
    setResult(null)
    setOpen(true)
    fetchPreview(nextRange)
  }
  const handleClose = () => {
    if (exporting) return
    setOpen(false)
  }
  React.useImperativeHandle(ref, () => ({ handleClickOpen }))

  const toggleAccount = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]))
  }

  // Active l'export sur un compte sans quitter la fenêtre (le réglage vivait
  // uniquement dans Paramètres → Comptes connectés).
  const toggleSyncOut = async (account, nextValue) => {
    const res = await makePatchRequest(
      `/api/user/calendar-accounts/${account.id}/sync-settings/`,
      { sync_out_enabled: nextValue, push_timezone: browserTimezone },
      {},
      true,
    )
    if (res && res.status === 200) {
      setPreview((prev) => ({
        ...prev,
        accounts: (prev.accounts || []).map((a) =>
          a.id === account.id ? { ...a, sync_out_enabled: nextValue } : a,
        ),
      }))
      if (nextValue && !selected.includes(account.id)) toggleAccount(account.id)
      if (!nextValue) setSelected((prev) => prev.filter((x) => x !== account.id))
    } else {
      showSnack('error', tr('errToggle', "Impossible de modifier ce compte."))
    }
  }

  const handleExport = async () => {
    setExporting(true)
    setResult(null)
    const res = await makePostRequest(
      '/api/user/push-events-to-external/',
      {
        start_date: range.start.format(API_DATE),
        end_date: range.end.format(API_DATE),
        acc_ids: selected,
        timezone: browserTimezone,
      },
      {},
      true,
    )
    setExporting(false)
    if (res && res.status === 200) {
      setResult(res.data)
      if (res.data.successful > 0 && res.data.failed === 0) {
        showSnack(
          'success',
          tr('okSnack', '{{count}} événement(s) envoyé(s).', { count: res.data.successful }),
        )
      }
      // Rafraîchit les compteurs « déjà synchronisé ».
      fetchPreview(range)
    } else {
      showSnack('error', tr('errExport', "L'export a échoué."))
    }
  }

  const accounts = (preview && preview.accounts) || []
  const eligible = accounts.filter((a) => selected.includes(a.id))
  const eventCount = eligible.length
    ? Math.max(...eligible.map((a) => a.event_count || 0))
    : (preview && preview.event_count) || 0

  const periodLabel = React.useMemo(() => {
    const s = range.start.clone().locale(culture)
    const e = range.end.clone().locale(culture)
    if (range.unit === 'day') return s.format('dddd D MMMM YYYY')
    if (range.unit === 'week') return `${s.format('D MMM')} → ${e.format('D MMM YYYY')}`
    if (range.unit === 'year') return s.format('YYYY')
    return s.format('MMMM YYYY')
  }, [range, culture])

  const unitLabel = {
    day: tr('unitDay', 'la journée affichée'),
    week: tr('unitWeek', 'la semaine affichée'),
    month: tr('unitMonth', 'le mois affiché'),
    year: tr('unitYear', "l'année affichée"),
  }[range.unit]

  return (
    <>
      {/* DirectionSnackbar s'ouvre au montage : on le monte à la demande (et on
          le re-monte via `key`) pour qu'un même message puisse réapparaître. */}
      {snack.open && (
        <DirectionSnackbar key={snack.message} message={snack.message} color={snack.color} />
      )}
      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IosShareIcon fontSize="small" />
          {tr('title', 'Exporter vers un agenda externe')}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary">
                {tr('periodHint', 'Vous exportez {{unit}}.', { unit: unitLabel })}
              </Typography>
              <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 0.6 }} flexWrap="wrap">
                <Chip
                  label={periodLabel}
                  color="primary"
                  variant="outlined"
                  sx={{ textTransform: 'capitalize', fontWeight: 700 }}
                />
                {loading ? (
                  <CircularProgress size={16} />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {tr('eventCount', '{{count}} événement(s) concerné(s)', {
                      count: eventCount,
                    })}
                  </Typography>
                )}
              </Stack>
            </Box>

            <Divider />

            {!loading && accounts.length === 0 && (
              <Alert severity="info">
                <AlertTitle>{tr('noAccountTitle', 'Aucun agenda connecté')}</AlertTitle>
                {tr(
                  'noAccountBody',
                  "Connectez un compte Google ou Outlook dans Paramètres → Comptes connectés, puis revenez ici.",
                )}
              </Alert>
            )}

            {accounts.map((account) => (
              <Box
                key={account.id}
                sx={{
                  border: '1px solid rgba(15,23,42,0.12)',
                  borderRadius: 2,
                  px: 1.5,
                  py: 1.1,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  flexWrap="wrap"
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selected.includes(account.id)}
                        disabled={!account.sync_out_enabled}
                        onChange={() => toggleAccount(account.id)}
                      />
                    }
                    label={
                      <Box>
                        <Typography sx={{ fontWeight: 700, fontSize: 14 }}>
                          {account.acc_type} — {account.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {account.user_name}
                        </Typography>
                      </Box>
                    }
                  />
                  <Stack alignItems="flex-end">
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={!!account.sync_out_enabled}
                          onChange={(e) => toggleSyncOut(account, e.target.checked)}
                        />
                      }
                      label={
                        <Typography variant="caption">
                          {tr('allowPush', 'Autoriser l\u2019export')}
                        </Typography>
                      }
                      sx={{ mr: 0 }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      {tr('accountCount', '{{count}} à envoyer · {{synced}} déjà synchronisé(s)', {
                        count: account.event_count,
                        synced: account.already_synced,
                      })}
                    </Typography>
                  </Stack>
                </Stack>
              </Box>
            ))}

            {result && (
              <Alert
                severity={result.failed > 0 ? (result.successful > 0 ? 'warning' : 'error') : 'success'}
                icon={result.failed > 0 ? <ErrorOutlineIcon /> : <CheckCircleIcon />}
              >
                <AlertTitle>
                  {tr('resultTitle', '{{ok}} envoyé(s), {{ko}} en échec', {
                    ok: result.successful,
                    ko: result.failed,
                  })}
                </AlertTitle>
                {(result.accounts || []).map((a) => (
                  <Typography key={a.account_id} variant="body2">
                    {a.acc_type} — {a.account_name} : {a.successful}/{a.processed}
                  </Typography>
                ))}
                {(result.errors || []).slice(0, 4).map((message, index) => (
                  <Typography key={index} variant="caption" display="block" sx={{ mt: 0.5 }}>
                    • {message}
                  </Typography>
                ))}
              </Alert>
            )}

            <Typography variant="caption" color="text.secondary">
              {tr(
                'footHint',
                "Seuls vos événements créés dans Life sont envoyés ; ceux importés d'un agenda externe ne repartent pas chez lui. Un événement déjà exporté est mis à jour, pas dupliqué.",
              )}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={exporting}>
            {tr('close', 'Fermer')}
          </Button>
          <Button
            variant="contained"
            onClick={handleExport}
            disabled={exporting || loading || selected.length === 0 || eventCount === 0}
            startIcon={
              exporting ? <CircularProgress size={16} color="inherit" /> : <IosShareIcon />
            }
          >
            {tr('btnConfirm', 'Exporter')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
})

ExportCalendarModal.displayName = 'ExportCalendarModal'

export default ExportCalendarModal
